// src/pages/CampaignOverview.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDialerStatus, startDialer, stopDialer } from '../services/dialerService';
import { deleteCampaign, getCampaigns } from '../services/campaignService';
import { getLeads, deleteLead, uploadLeads } from '../services/leadsService';
import CampaignWorkflowTable from '../components/CampaignWorkflowTable';

const CampaignOverview = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // Campaign state
    const [campaign, setCampaign] = useState(null);

    // Dialer/KPI state
    const [dialerStatus, setDialerStatus] = useState(null);
    const [loadingDialer, setLoadingDialer] = useState(false);
    const [campaignControlStatus, setCampaignControlStatus] = useState('idle');

    // Leads management state
    const [leads, setLeads] = useState([]);
    const [filteredLeads, setFilteredLeads] = useState([]);
    const [leadsSearchTerm, setLeadsSearchTerm] = useState('');
    const [loadingLeads, setLoadingLeads] = useState(false);

    // Modal states
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);

    // Dialer settings
    const [concurrency, setConcurrency] = useState(1);

    // Fetch campaign details
    const fetchCampaign = useCallback(async () => {
        try {
            const response = await getCampaigns();
            const foundCampaign = response.data.campaigns.find(c => c.id === Number(id));
            if (foundCampaign) {
                setCampaign(foundCampaign);
            }
        } catch (error) {
            console.error('Error fetching campaign:', error);
        }
    }, [id]);

    // Fetch dialer status
    const fetchDialerStatus = useCallback(async () => {
        setLoadingDialer(true);
        try {
            const response = await getDialerStatus(id);
            setDialerStatus(response.data.status);
            if (response.data.status.campaignActive) {
                setCampaignControlStatus('running');
            }
        } catch (error) {
            console.error('Error fetching dialer status:', error);
        } finally {
            setLoadingDialer(false);
        }
    }, [id]);

    // Fetch campaign leads
    const fetchCampaignLeads = useCallback(async () => {
        setLoadingLeads(true);
        try {
            const response = await getLeads();
            const campaignLeads = response.data.leads.filter(lead => {
                if (lead.campaignId === null || lead.campaignId === undefined || lead.campaignId === "") {
                    return true;
                }
                return Number(lead.campaignId) === Number(id);
            });
            setLeads(campaignLeads);
            setFilteredLeads(campaignLeads);
        } catch (error) {
            console.error('Error fetching leads:', error);
        } finally {
            setLoadingLeads(false);
        }
    }, [id]);

    // Initialize data
    useEffect(() => {
        fetchCampaign();
        fetchDialerStatus();
        fetchCampaignLeads();

        // Set up polling for dialer status
        const intervalId = setInterval(fetchDialerStatus, 5000);
        return () => clearInterval(intervalId);
    }, [fetchCampaign, fetchDialerStatus, fetchCampaignLeads]);

    // Campaign control handlers
    const handleStartCampaign = async () => {
        try {
            const phoneNumbers = leads
                .map(lead => lead.phoneNumber || lead.phone)
                .filter(num => !!num);

            if (phoneNumbers.length === 0) {
                alert('No valid phone numbers found in leads.');
                return;
            }

            await startDialer({
                campaignId: id,
                maxConcurrentCalls: concurrency,
                phoneNumbers
            });
            setCampaignControlStatus('running');
            fetchDialerStatus();
        } catch (error) {
            console.error('Error starting campaign:', error);
            alert('Failed to start campaign');
        }
    };

    const handlePauseCampaign = async () => {
        try {
            await stopDialer({ campaignId: id });
            setCampaignControlStatus('paused');
            fetchDialerStatus();
        } catch (error) {
            console.error('Error pausing campaign:', error);
            alert('Failed to pause campaign');
        }
    };

    const handleStopCampaign = async () => {
        try {
            await stopDialer({ campaignId: id });
            setCampaignControlStatus('stopped');
            fetchDialerStatus();
        } catch (error) {
            console.error('Error stopping campaign:', error);
            alert('Failed to stop campaign');
        }
    };

    const handleDeleteCampaign = async () => {
        if (window.confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) {
            try {
                await deleteCampaign(id);
                navigate('/campaigns');
            } catch (error) {
                console.error('Error deleting campaign:', error);
                alert('Failed to delete campaign');
            }
        }
    };

    // Leads management handlers
    const handleLeadsSearch = (e) => {
        const term = e.target.value;
        setLeadsSearchTerm(term);
        if (term.trim() === '') {
            setFilteredLeads(leads);
        } else {
            const lowerTerm = term.toLowerCase();
            const filtered = leads.filter(lead =>
                (lead.firstName && lead.firstName.toLowerCase().includes(lowerTerm)) ||
                (lead.lastName && lead.lastName.toLowerCase().includes(lowerTerm)) ||
                ((lead.phoneNumber || lead.phone)?.toString().includes(lowerTerm))
            );
            setFilteredLeads(filtered);
        }
    };

    const handleDeleteLead = async (leadId) => {
        if (window.confirm('Are you sure you want to delete this lead?')) {
            try {
                await deleteLead(leadId);
                fetchCampaignLeads();
            } catch (error) {
                console.error('Error deleting lead:', error);
                alert('Failed to delete lead');
            }
        }
    };

    // Upload modal handlers
    const handleUploadLeads = async () => {
        if (!uploadFile) {
            alert('Please select a CSV file to upload.');
            return;
        }

        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('campaignId', id);

        try {
            await uploadLeads(formData);
            await fetchCampaignLeads();
            setIsUploadModalOpen(false);
            setUploadFile(null);
            alert('Leads uploaded successfully');
        } catch (error) {
            console.error('Error uploading leads:', error);
            alert('Failed to upload leads');
        }
    };

    // Format the KPI data
    const kpis = {
        totalCalls: dialerStatus?.totalCalls || 0,
        answeredCalls: dialerStatus?.answeredCalls || 0,
        missedCalls: dialerStatus?.missedCalls || 0,
        conversionRate: dialerStatus?.conversionRate ? `${dialerStatus.conversionRate}%` : '0%',
        averageCallDuration: dialerStatus?.averageCallDuration || '0s',
    };

    return (
        <div style={{ padding: '20px' }}>
            {/* Campaign Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Campaign Overview: {campaign?.name || id}</h2>
                <div>
                    <button className="create-campaign-btn" onClick={() => setIsUploadModalOpen(true)}>
                        Upload Leads
                    </button>
                </div>
            </div>

            {/* Dialer Controls */}
            <div className="dialer-controls" style={{ marginBottom: '20px' }}>
                <div style={{ marginBottom: '10px' }}>
                    <label style={{ fontWeight: '700', marginRight: '10px' }}>
                        Concurrent Calls:
                    </label>
                    <select
                        value={concurrency}
                        onChange={(e) => setConcurrency(Number(e.target.value))}
                        style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                            <option key={num} value={num}>{num}</option>
                        ))}
                    </select>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        className="create-campaign-btn"
                        onClick={handleStartCampaign}
                        disabled={campaignControlStatus === 'running'}
                    >
                        Start Campaign
                    </button>
                    <button
                        className="create-campaign-btn"
                        onClick={handlePauseCampaign}
                        disabled={campaignControlStatus !== 'running'}
                    >
                        Pause Campaign
                    </button>
                    <button
                        className="create-campaign-btn"
                        onClick={handleStopCampaign}
                        disabled={campaignControlStatus === 'stopped'}
                    >
                        Stop Campaign
                    </button>
                    <button className="edit-btn" onClick={handleDeleteCampaign}>
                        Delete Campaign
                    </button>
                </div>
            </div>

            {/* KPIs Display */}
            <div className="kpi-section" style={{ marginBottom: '30px' }}>
                <h3>Campaign Metrics</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                    <div className="kpi-card">
                        <h4>Total Calls</h4>
                        <p>{kpis.totalCalls}</p>
                    </div>
                    <div className="kpi-card">
                        <h4>Answered Calls</h4>
                        <p>{kpis.answeredCalls}</p>
                    </div>
                    <div className="kpi-card">
                        <h4>Missed Calls</h4>
                        <p>{kpis.missedCalls}</p>
                    </div>
                    <div className="kpi-card">
                        <h4>Conversion Rate</h4>
                        <p>{kpis.conversionRate}</p>
                    </div>
                    <div className="kpi-card">
                        <h4>Avg Call Duration</h4>
                        <p>{kpis.averageCallDuration}</p>
                    </div>
                </div>
            </div>

            {/* Workflow Table */}
            <CampaignWorkflowTable campaignId={id} />

            {/* Leads Table */}
            <div style={{ marginTop: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3>Campaign Leads</h3>
                    <input
                        type="text"
                        placeholder="Search leads..."
                        value={leadsSearchTerm}
                        onChange={handleLeadsSearch}
                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '200px' }}
                    />
                </div>

                <table className="campaign-table">
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Status</th>
                        <th>Last Called</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredLeads.map((lead) => {
                        const leadPhone = lead.phoneNumber || lead.phone;
                        const callRecord = dialerStatus?.callRecords?.find(
                            cr => cr.phoneNumber === leadPhone
                        );
                        return (
                            <tr key={lead.id}>
                                <td>{`${lead.firstName} ${lead.lastName}`}</td>
                                <td>{leadPhone}</td>
                                <td>{callRecord?.status || 'Not Called'}</td>
                                <td>
                                    {callRecord?.timestamp
                                        ? new Date(callRecord.timestamp).toLocaleString()
                                        : '-'
                                    }
                                </td>
                                <td>
                                    <button
                                        className="edit-btn"
                                        onClick={() => handleDeleteLead(lead.id)}
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>

            {/* Upload Modal */}
            {isUploadModalOpen && (
                <div className="modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Upload Leads</h3>
                        </div>
                        <div className="modal-body">
                            <p>Select a CSV file with columns: <strong>firstName, lastName, phoneNumber</strong></p>
                            <input
                                type="file"
                                accept=".csv"
                                onChange={(e) => setUploadFile(e.target.files[0])}
                            />
                        </div>
                        <div className="modal-footer">
                            <button onClick={() => setIsUploadModalOpen(false)}>Cancel</button>
                            <button onClick={handleUploadLeads}>Upload</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CampaignOverview;